import type { FormVersionDto } from "../../services/ApiService";
import type { FormVersion } from "../../state/formVersion";
import type { IAtomicChange } from "json-diff-ts";
import { atomizeChangeset, diff, Operation } from "json-diff-ts";
import { toFormVersionDto } from "./formVersion";
import logger from "../logger";
import { isDate, isEqual, isFunction, isNil, isString } from "lodash-es";
import type { WidgetUid } from "../../types";

type AllowedValue<T> = T | ((value: T) => boolean);
type AllowedChange = {
  path: AllowedValue<string>;
  value: AllowedValue<unknown>;
} & ({ type: Operation.ADD | Operation.REMOVE } | { type: Operation.UPDATE; oldValue: AllowedValue<unknown> });

const valueIsIrrelevant = () => true;

const fieldProperty = (widget: WidgetUid, property: string) => (value: string) =>
  value.endsWith(`.fields[${widget}].properties.${property}`);

const allowedChanges: AllowedChange[] = [
  // Fields
  { type: Operation.ADD, path: fieldProperty("com.moreapps:lookup:1", "default_value"), value: undefined },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:detail:1", "min_items"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:detail:1", "max_items"), value: null },
  { type: Operation.REMOVE, path: fieldProperty("com.moreapps:detail:1", "form"), value: valueIsIrrelevant },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:detail:1", "target_form_id"), value: isString },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:image:1", "label_text"), value: "" },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:image:1", "click_to_view"), value: false },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:image:1", "max_width"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:image:1", "max_height"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:checkbox:1", "required"), value: false },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:radio:1", "default_value"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:search:1", "filter_fields"), value: [] },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:photo:1", "save_to_gallery"), value: false },
  {
    type: Operation.REMOVE,
    path: fieldProperty("com.moreapps:photo:1", "full_size_preview"),
    value: valueIsIrrelevant,
  },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:number:1", "text_default_value"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:number:1", "numeric_min"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:number:1", "numeric_max"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:pin:1", "default_image"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:text_area:1", "min_length"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.dirkjanhoek:price:4", "min"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.dirkjanhoek:price:4", "max"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps.plugins:drawing:5", "background_image"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:text:1", "min_length"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:text:1", "max_length"), value: null },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:lookup:1", "expand"), value: false },
  { type: Operation.ADD, path: fieldProperty("com.moreapps:lookup:1", "default_value"), value: [] },

  // Random data on fields within subforms
  {
    type: Operation.REMOVE,
    path: (value) => value.startsWith("$.fieldProperties") && value.endsWith(".$depth"),
    value: valueIsIrrelevant,
  },
  {
    type: Operation.REMOVE,
    path: (value) => value.startsWith("$.fieldProperties") && value.endsWith(".icon"),
    value: valueIsIrrelevant,
  },
  {
    type: Operation.REMOVE,
    path: (value) => value.startsWith("$.fieldProperties") && value.endsWith(".dataType"),
    value: valueIsIrrelevant,
  },
  {
    type: Operation.REMOVE,
    path: (value) => value.startsWith("$.fieldProperties") && value.endsWith(".data"),
    value: valueIsIrrelevant,
  },

  // Rules
  { type: Operation.REMOVE, path: "$.rules[].conditions[].fieldUid", value: null },
  { type: Operation.REMOVE, path: "$.rules[].conditions[].fieldObjectKey", value: null },

  // Email integration
  { type: Operation.REMOVE, path: "$.triggers[].fromName", value: null },
  { type: Operation.REMOVE, path: "$.triggers[].replyTo", value: null },
  { type: Operation.UPDATE, path: "$.triggers[].carbonCopyRecipients", value: [], oldValue: null },
  { type: Operation.UPDATE, path: "$.triggers[].blindCarbonCopyRecipients", value: [], oldValue: null },
  { type: Operation.REMOVE, path: "$.triggers[].pdfContent", value: null },
  { type: Operation.REMOVE, path: "$.triggers[].pdfHeaderLogoResourceId", value: null },

  // Other integrations
  { type: Operation.ADD, path: "$.integrations[excel].configuration.template", value: undefined },
  { type: Operation.ADD, path: "$.integrations[excel].configuration.fromName", value: undefined },
  { type: Operation.ADD, path: "$.integrations[excel].configuration.replyTo", value: undefined },
  { type: Operation.ADD, path: "$.integrations[excel].configuration.recipients", value: "" },
  { type: Operation.ADD, path: "$.integrations[excel].configuration.dynamicRecipients", value: [] },
  { type: Operation.ADD, path: "$.integrations[excel].configuration.cc", value: "" },
  { type: Operation.ADD, path: "$.integrations[excel].configuration.bcc", value: "" },
  { type: Operation.REMOVE, path: "$.integrations[excel].configuration.attachImages", value: valueIsIrrelevant },
  { type: Operation.ADD, path: "$.integrations[word].configuration.template", value: undefined },
  { type: Operation.ADD, path: "$.integrations[word].configuration.fromName", value: undefined },
  { type: Operation.ADD, path: "$.integrations[word].configuration.replyTo", value: undefined },
  { type: Operation.ADD, path: "$.integrations[word].configuration.recipients", value: "" },
  { type: Operation.ADD, path: "$.integrations[word].configuration.dynamicRecipients", value: [] },
  { type: Operation.ADD, path: "$.integrations[word].configuration.cc", value: "" },
  { type: Operation.ADD, path: "$.integrations[word].configuration.bcc", value: "" },
  { type: Operation.ADD, path: "$.integrations[googlesheetupdate].configuration.serviceAccount", value: undefined },
  { type: Operation.ADD, path: "$.integrations[googlesheetupdate].configuration.searchWidget", value: undefined },
  { type: Operation.ADD, path: "$.integrations[googlesheetupdate].configuration.newValueWidget", value: undefined },
  { type: Operation.ADD, path: "$.integrations[googlesheetupdate].configuration.columnName", value: undefined },

  // Other
  { type: Operation.UPDATE, path: "$.meta.lastUpdated", value: isDate, oldValue: isDate },
  { type: Operation.UPDATE, path: "$.meta.lastUpdatedWith", value: isString, oldValue: valueIsIrrelevant },
  { type: Operation.UPDATE, path: "$.meta.created", value: isDate, oldValue: isDate },
  { type: Operation.REMOVE, path: "$.meta.createdWith", value: null },
  { type: Operation.REMOVE, path: "$.settings.icon", value: valueIsIrrelevant },
  { type: Operation.UPDATE, path: "$.settings.itemHtml", value: "", oldValue: null },
  { type: Operation.UPDATE, path: "$.settings.searchSettings.enabled", value: false, oldValue: null },
  { type: Operation.UPDATE, path: "$.settings.searchSettings.onlyForCurrentUser", value: false, oldValue: null },
  { type: Operation.UPDATE, path: "$.settings.searchSettings.filteringEnabled", value: false, oldValue: null },
  { type: Operation.UPDATE, path: "$.settings.searchSettings.filteredFields", value: [], oldValue: null },
  { type: Operation.REMOVE, path: "$.theme.system", value: valueIsIrrelevant },
  { type: Operation.REMOVE, path: "$.theme.name", value: valueIsIrrelevant },
  { type: Operation.REMOVE, path: "$.theme.colors", value: valueIsIrrelevant },
  { type: Operation.REMOVE, path: "$.theme.defaultTheme", value: valueIsIrrelevant },
  { type: Operation.REMOVE, path: "$.dependencies", value: valueIsIrrelevant },
];

const arrayPathRegex = /(?<property>[a-zA-Z_]+)\[(?<index>\d+)]/;
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const toPathIndex = (value: Record<string, any>, property: string, index: string) => {
  if (property === "fields") {
    return value[property][index]["widget"];
  } else if (property === "integrations") {
    return value[property][index]["key"];
  } else {
    return "";
  }
};

const isValueEqual = (allowedValue: AllowedValue<unknown>, actualValue: unknown): boolean =>
  isFunction(allowedValue) ? allowedValue(actualValue) : isEqual(allowedValue, actualValue);

export const detectMappingIssues = (originalFormVersion: FormVersionDto, mappedFormVersion: FormVersion) => {
  new Promise(() => {
    const diffResult = diff(originalFormVersion, toFormVersionDto(mappedFormVersion));
    const flattenedDiff = atomizeChangeset(diffResult);

    // 'atomizeChangeset' changes 'UPDATE' to two separate operations ('ADD' and 'REMOVE'). This transforms them back...
    const diffWithUpdates = Object.values(
      flattenedDiff.reduce(
        (acc, item) => {
          if (isNil(acc[item.path])) {
            acc[item.path] = { ...item };
          } else {
            acc[item.path] = {
              ...item,
              type: Operation.UPDATE,
              oldValue: item.type === Operation.REMOVE ? item.value : acc[item.path].value,
            };
          }
          return acc;
        },
        {} as Record<string, IAtomicChange>,
      ),
    );

    const unexpectedChanges = diffWithUpdates.filter((changeset) => {
      let { path } = changeset.path.split(".").reduce(
        ({ path, value }, cur) => {
          if (cur === "$") {
            return { path: cur, value: originalFormVersion };
          }

          const matchesArray = cur.match(arrayPathRegex);
          if (!isNil(matchesArray)) {
            const { property, index } = matchesArray.groups!;
            return {
              path: path + "." + property + "[" + toPathIndex(value, property, index) + "]",
              value: value[property][index],
            };
          }

          return { path: path + "." + cur, value: value[cur] };
        },
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        { path: "", value: originalFormVersion as Record<string, any> },
      );

      // In certain cases (for example when a whole object is removed) the path is not the full path and is missing
      // the actual property.
      if (!path.endsWith(changeset.key)) {
        path += "." + changeset.key;
      }

      return !allowedChanges.some((allowedChange) => {
        const isOldValueEqual =
          allowedChange.type === Operation.UPDATE ? isValueEqual(allowedChange.oldValue, changeset.oldValue) : true;

        return (
          allowedChange.type === changeset.type &&
          isValueEqual(allowedChange.path, path) &&
          isValueEqual(allowedChange.value, changeset.value) &&
          isOldValueEqual
        );
      });
    });

    if (unexpectedChanges.length > 0) {
      logger.sendToLogCollector("FormVersion mapping resulted in unexpected changes", {
        formVersionId: originalFormVersion.id,
        unexpectedChanges,
      });
    }
  }).catch((e: unknown) => {
    logger.error("Failed to detect mapping issues", e);
  });
};
